import type { AthenaAuthDatabase } from "./database.ts";
import { assertQueryResult } from "./database.ts";
import type { SchemaExpectation } from "./schema-manifest.ts";

export type AthenaAuthSchemaDriftKind =
  | "missing-schema"
  | "missing-table"
  | "missing-column"
  | "column-mismatch"
  | "missing-index"
  | "missing-constraint"
  | "constraint-mismatch";

export interface AthenaAuthSchemaDrift {
  actual?: unknown;
  expected?: unknown;
  kind: AthenaAuthSchemaDriftKind;
  object: string;
}

interface CatalogSnapshot {
  columns: Set<string>;
  constraints: Set<string>;
  constraintDefinitions: Map<string, string>;
  indexes: Set<string>;
  schemas: Set<string>;
  tables: Set<string>;
}

const INSPECT_SAVEPOINT = "athena_auth_schema_inspect";

function postgresSqlState(error: unknown): string {
  if (error && typeof error === "object" && "code" in error) {
    const code = (error as { code?: unknown }).code;
    if (typeof code === "string" && code.trim()) {
      return code.trim().toUpperCase();
    }
  }
  return "";
}

/**
 * Physical catalog probes must not abort an in-flight Auth migrate transaction.
 * PostgreSQL treats any statement error as fatal for the rest of the transaction.
 */
async function withOptionalInspectSavepoint<T>(
  db: AthenaAuthDatabase,
  run: () => Promise<T>
): Promise<T> {
  let isolated = false;
  try {
    await db.query(`SAVEPOINT ${INSPECT_SAVEPOINT}`);
    isolated = true;
  } catch (error) {
    if (postgresSqlState(error) !== "25P01") {
      throw error;
    }
  }
  try {
    const value = await run();
    if (isolated) {
      await db.query(`RELEASE SAVEPOINT ${INSPECT_SAVEPOINT}`);
    }
    return value;
  } catch (error) {
    if (isolated) {
      try {
        await db.query(`ROLLBACK TO SAVEPOINT ${INSPECT_SAVEPOINT}`);
        await db.query(`RELEASE SAVEPOINT ${INSPECT_SAVEPOINT}`);
      } catch {
        // Session may already be failed closed.
      }
    }
    throw error;
  }
}

async function loadCatalogSnapshot(
  db: AthenaAuthDatabase
): Promise<CatalogSnapshot> {
  const schemasResult = assertQueryResult<{ nspname: string }>(
    await db.query<{ nspname: string }>(
      `SELECT nspname FROM pg_catalog.pg_namespace WHERE nspname = 'athena'`
    ),
    "schema-inspect schemas"
  );

  const tablesResult = assertQueryResult<{
    table_schema: string;
    table_name: string;
  }>(
    await db.query<{ table_schema: string; table_name: string }>(
      `SELECT table_schema, table_name
       FROM information_schema.tables
       WHERE table_schema = 'athena'
         AND table_type = 'BASE TABLE'`
    ),
    "schema-inspect tables"
  );

  const columnsResult = assertQueryResult<{
    table_schema: string;
    table_name: string;
    column_name: string;
  }>(
    await db.query<{
      table_schema: string;
      table_name: string;
      column_name: string;
    }>(
      `SELECT table_schema, table_name, column_name
       FROM information_schema.columns
       WHERE table_schema = 'athena'`
    ),
    "schema-inspect columns"
  );

  const indexesResult = assertQueryResult<{
    schemaname: string;
    indexname: string;
  }>(
    await db.query<{ schemaname: string; indexname: string }>(
      `SELECT schemaname, indexname
       FROM pg_catalog.pg_indexes
       WHERE schemaname = 'athena'`
    ),
    "schema-inspect indexes"
  );

  const constraintsResult = assertQueryResult<{
    table_schema: string;
    table_name: string;
    constraint_name: string;
  }>(
    await db.query<{
      table_schema: string;
      table_name: string;
      constraint_name: string;
    }>(
      `SELECT table_schema, table_name, constraint_name
       FROM information_schema.table_constraints
       WHERE table_schema = 'athena'`
    ),
    "schema-inspect constraints"
  );
  const constraintDefinitionsResult = assertQueryResult<{
    definition: string;
    table_schema: string;
    table_name: string;
    constraint_name: string;
  }>(
    await db.query<{
      definition: string;
      table_schema: string;
      table_name: string;
      constraint_name: string;
    }>(
      `SELECT n.nspname AS table_schema,
              rel.relname AS table_name,
              pg_con.conname AS constraint_name,
              pg_get_constraintdef(pg_con.oid) AS definition
       FROM pg_catalog.pg_constraint AS pg_con
       JOIN pg_catalog.pg_class AS rel
         ON rel.oid = pg_con.conrelid
       JOIN pg_catalog.pg_namespace AS n
         ON n.oid = rel.relnamespace
       WHERE n.nspname = 'athena'`
    ),
    "schema-inspect constraint definitions"
  );

  return {
    columns: new Set(
      columnsResult.rows.map(
        (row) => `${row.table_schema}.${row.table_name}.${row.column_name}`
      )
    ),
    constraints: new Set(
      constraintsResult.rows.map(
        (row) => `${row.table_schema}.${row.table_name}.${row.constraint_name}`
      )
    ),
    constraintDefinitions: new Map(
      constraintDefinitionsResult.rows.map((row) => [
        `${row.table_schema}.${row.table_name}.${row.constraint_name}`,
        row.definition,
      ])
    ),
    indexes: new Set(
      indexesResult.rows.map((row) => `${row.schemaname}.${row.indexname}`)
    ),
    schemas: new Set(schemasResult.rows.map((row) => row.nspname)),
    tables: new Set(
      tablesResult.rows.map((row) => `${row.table_schema}.${row.table_name}`)
    ),
  };
}

function evaluateExpectation(
  expectation: SchemaExpectation,
  catalog: CatalogSnapshot
): AthenaAuthSchemaDrift | undefined {
  switch (expectation.kind) {
    case "schema": {
      const name = expectation.schema ?? expectation.name ?? "";
      if (!catalog.schemas.has(name)) {
        return {
          expected: name,
          kind: "missing-schema",
          object: expectation.object,
        };
      }
      return;
    }
    case "table": {
      if (!catalog.tables.has(expectation.object)) {
        return {
          expected: expectation.object,
          kind: "missing-table",
          object: expectation.object,
        };
      }
      return;
    }
    case "column": {
      if (!catalog.columns.has(expectation.object)) {
        return {
          expected: expectation.object,
          kind: "missing-column",
          object: expectation.object,
        };
      }
      return;
    }
    case "index": {
      if (!catalog.indexes.has(expectation.object)) {
        return {
          expected: expectation.object,
          kind: "missing-index",
          object: expectation.object,
        };
      }
      return;
    }
    case "constraint": {
      if (!catalog.constraints.has(expectation.object)) {
        return {
          expected: expectation.object,
          kind: "missing-constraint",
          object: expectation.object,
        };
      }
      if (expectation.definition != null) {
        const actual = catalog.constraintDefinitions.get(expectation.object);
        if (
          actual == null ||
          !normalizeDefinition(actual).includes(
            normalizeDefinition(expectation.definition)
          )
        ) {
          return {
            actual,
            expected: expectation.definition,
            kind: "constraint-mismatch",
            object: expectation.object,
          };
        }
      }
      return;
    }
    default:
      return;
  }
}

function normalizeDefinition(definition: string): string {
  return definition.replace(/\s+/g, " ").trim().toLowerCase();
}

/**
 * Compare structural expectations against live PostgreSQL catalogs.
 */
export async function inspectAthenaAuthExpectations(
  db: AthenaAuthDatabase,
  expectations: readonly SchemaExpectation[]
): Promise<AthenaAuthSchemaDrift[]> {
  if (expectations.length === 0) {
    return [];
  }
  const byMigration = await inspectAthenaAuthMigrationExpectations(db, [
    { expectations, version: 0 },
  ]);
  return byMigration.get(0) ?? [];
}

export async function inspectAthenaAuthMigrationExpectations(
  db: AthenaAuthDatabase,
  expectationSets: readonly {
    expectations: readonly SchemaExpectation[];
    version: number;
  }[]
): Promise<ReadonlyMap<number, AthenaAuthSchemaDrift[]>> {
  return withOptionalInspectSavepoint(db, async () => {
    const catalog = await loadCatalogSnapshot(db);
    const driftByMigration = new Map<number, AthenaAuthSchemaDrift[]>();
    for (const set of expectationSets) {
      if (set.expectations.length === 0) {
        continue;
      }
      const drift: AthenaAuthSchemaDrift[] = [];
      for (const expectation of set.expectations) {
        const item = evaluateExpectation(expectation, catalog);
        if (item) {
          drift.push(item);
        }
      }
      if (drift.length > 0) {
        driftByMigration.set(set.version, drift);
      }
    }
    return driftByMigration;
  });
}
